/**
 * Compress an image File → base64 JPEG data URL.
 *  - Downscales so longest edge ≤ MAX_EDGE
 *  - Iteratively drops JPEG quality until size ≤ TARGET_BYTES
 * Returns { dataUrl, size, mimetype } or throws on failure.
 *
 * Shared by LightRichEditor (CR description) and CRCommentsThread (comments)
 * so pasted screenshots stay well below 1 MB before being embedded as
 * inline <img src="data:image/jpeg;base64,…"> in the HTML payload.
 */
export async function compressImageFile(file, {
  MAX_EDGE = 1600,
  TARGET_BYTES = 900 * 1024,       // ~900 KB target → stays comfortably under 1 MB
  MIN_QUALITY = 0.4,
} = {}) {
  const bmp = await createImageBitmap(file).catch(async () => {
    return await new Promise((res, rej) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => { URL.revokeObjectURL(url); res(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); rej(e); };
      img.src = url;
    });
  });
  const w0 = bmp.width, h0 = bmp.height;
  const scale = Math.min(1, MAX_EDGE / Math.max(w0, h0));
  const w = Math.round(w0 * scale);
  const h = Math.round(h0 * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";       // flatten alpha for JPEG
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bmp, 0, 0, w, h);

  let q = 0.85;
  let dataUrl = canvas.toDataURL("image/jpeg", q);
  let bytes = dataUrlBytes(dataUrl);
  while (bytes > TARGET_BYTES && q > MIN_QUALITY) {
    q = Math.max(MIN_QUALITY, q - 0.12);
    dataUrl = canvas.toDataURL("image/jpeg", q);
    bytes = dataUrlBytes(dataUrl);
  }
  return { dataUrl, size: bytes, mimetype: "image/jpeg" };
}

function dataUrlBytes(dataUrl) {
  const i = dataUrl.indexOf(",");
  if (i < 0) return dataUrl.length;
  const b64 = dataUrl.slice(i + 1);
  const pad = (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);
  return Math.floor((b64.length * 3) / 4) - pad;
}
